/**
 * `<smootify-product>` Web Component.
 *
 * Mirrors the legacy Smootify product host so that every product-bound
 * attribute (product="...", variant="...", option="...", condition="...",
 * [action="add-to-cart"], ...) can find its ancestor and pull data from it.
 *
 * Lifecycle:
 *   - `connectedCallback()` resolves the handle (attribute or URL), fetches
 *     the product, hydrates children, and dispatches
 *     `smootify:product_loaded` on `document`.
 *   - Listens to `changeVariant` events bubbling from option selectors,
 *     updates `selectedVariant`, re-hydrates variant + condition subtrees,
 *     and dispatches `smootify:variant_changed`.
 *   - `data-state` reflects the loading phase (`"default"` / `"loading"`).
 */

import { storefront } from "../storefront/client.js";
import {
  PRODUCT_BY_HANDLE,
  PRODUCT_RECOMMENDATIONS,
  PRODUCTS_LIGHT_BY_HANDLES,
  ALL_PRODUCTS,
} from "../storefront/queries.js";
import { config } from "../config.js";
import { resolveProductHandleFromURL } from "../attributes/scanner.js";
import { hydrateProduct } from "../attributes/product.js";
import {
  hydrateVariant,
  pickDefaultVariant,
} from "../attributes/variant.js";
import {
  hydrateOptions,
  syncSelectionToVariant,
} from "../attributes/option.js";
import {
  applyConditions,
  buildConditionState,
} from "../attributes/condition.js";
import type { ChangeVariantDetail } from "../attributes/option.js";
import type { Product, ProductVariant } from "../types.js";
import { getLastViewedHandles, pushLastViewed } from "./last-viewed.js";
import { getWishlistHandles } from "./wishlist-toggle.js";
import { getCurrentCart } from "../cart/state.js";

// ---------------------------------------------------------------------------
//  Helpers
// ---------------------------------------------------------------------------

function isDev(): boolean {
  return config.isDev;
}

function warn(...args: unknown[]): void {
  if (isDev()) {
    // eslint-disable-next-line no-console
    console.warn("[smootify-product]", ...args);
  }
}

function isMobileViewport(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  return window.matchMedia("(max-width: 767px)").matches;
}

function isHiddenByAuthorCss(el: Element): boolean {
  if (typeof window === "undefined") return false;
  let node: Element | null = el;
  while (node && node !== document.documentElement) {
    const style = window.getComputedStyle(node);
    if (style.display === "none" || style.visibility === "hidden") {
      return true;
    }
    node = node.parentElement;
  }
  return false;
}

function shouldSkipHiddenCartUpsells(el: Element): boolean {
  return isMobileViewport() && isHiddenByAuthorCss(el);
}

// ---------------------------------------------------------------------------
//  Element
// ---------------------------------------------------------------------------

export class SmootifyProductElement extends HTMLElement {
  /** Raw product from Storefront. `null` until `connectedCallback` resolves. */
  product: Product | null = null;
  /** Currently-selected variant. `null` when no product or no variants. */
  selectedVariant: ProductVariant | null = null;

  private _loaded = false;
  private _cartUpsellsMq: MediaQueryList | null = null;
  private _onCartUpsellsBreakpointChange = (): void => {
    if (!shouldSkipHiddenCartUpsells(this)) {
      void this.loadMany("cart-upsells");
    }
  };
  private _onChangeVariant = (ev: Event): void => {
    const detail = (ev as CustomEvent<ChangeVariantDetail>).detail;
    if (!detail || !this.product) return;
    if (detail.variant) {
      this.applyVariant(detail.variant);
      this.dispatchEvent(
        new CustomEvent("smootify:variant_changed", {
          detail: { variant: detail.variant, product: this.product },
          bubbles: true,
        }),
      );
    }
  };

  connectedCallback(): void {
    if (this._loaded) return;
    this._loaded = true;

    this.setAttribute("data-state", "loading");
    this.addEventListener("changeVariant", this._onChangeVariant);

    // Détecte les blocs "liste de produits" (related / last-viewed / cart-upsells
    // / wishlist / filter). Ce sont des TEMPLATES qui doivent être clonés 1
    // fois par produit fetché. On évite la récursion via data-lmdr-multi-clone
    // sur les clones.
    //   - "filter" : page catalogue complète (`/all-products`) — fetch tous
    //     les produits via Storefront `products(first: N)`.
    const dataId = this.getAttribute("data-id");
    const MULTI_IDS = new Set([
      "related",
      "last-viewed",
      "cart-upsells",
      "wishlist",
      "filter",
    ]);
    if (
      dataId !== null &&
      MULTI_IDS.has(dataId) &&
      !this.hasAttribute("data-lmdr-multi-clone")
    ) {
      // Pour wishlist, on s'abonne aussi aux updates pour re-render à la volée
      // (ex: retirer un item depuis la page /wishlist elle-même).
      if (dataId === "wishlist") {
        document.addEventListener("lmdr:wishlist_updated", this._onWishlistUpdated);
      }
      // Pour cart-upsells, on re-fetch les recos dès qu'un produit est
      // ajouté / retiré / updated dans le panier.
      if (dataId === "cart-upsells") {
        document.addEventListener("smootify:cart_updated", this._onCartUpdated);
      }
      this.loadManyWhenAllowed(dataId);
      return;
    }

    // LAZY HYDRATION : sur les clones filter (page catalogue 1500+ cards),
    // on ne fetch pas immédiatement. Un IntersectionObserver dans loadMany
    // déclenchera loadProduct() quand le clone entre dans le viewport.
    // Style Smootify natif : seuls les visibles sont hydratés → boot rapide.
    if (
      this.hasAttribute("data-lmdr-multi-clone") &&
      this.getAttribute("data-lmdr-multi-kind") === "filter" &&
      !this.hasAttribute("data-lmdr-allow-hydration")
    ) {
      this.setAttribute("data-state", "default");
      this._loaded = false; // re-permettre l'auto-load au "allow"
      return;
    }

    void this.loadProduct();
  }

  /**
   * Force l'hydratation d'un clone lazy (filter). Appelé par l'IntersectionObserver
   * quand le clone entre dans le viewport.
   *
   * Skip si la card est pagination-hidden (page > 1) ou filter-hidden : on
   * attendra qu elle redevienne visible (re-observe via MutationObserver dans
   * loadMany) pour eviter des dizaines de fetch inutiles au boot quand les
   * 1500 clones sont initialement empiles avant que pagination CSS ne s applique.
   */
  public hydrateLazyNow(): void {
    if (this._loaded) return;
    if (
      this.classList.contains("lmdr-pagination-hidden") ||
      this.classList.contains("lmdr-filter-hidden")
    ) {
      return;
    }
    this._loaded = true;
    this.setAttribute("data-lmdr-allow-hydration", "true");
    this.setAttribute("data-state", "loading");
    void this.loadProduct();
  }

  disconnectedCallback(): void {
    this.removeEventListener("changeVariant", this._onChangeVariant);
    document.removeEventListener("lmdr:wishlist_updated", this._onWishlistUpdated);
    document.removeEventListener("smootify:cart_updated", this._onCartUpdated);
    if (this._cartUpsellsMq) {
      this._cartUpsellsMq.removeEventListener?.(
        "change",
        this._onCartUpsellsBreakpointChange,
      );
      this._cartUpsellsMq.removeListener?.(this._onCartUpsellsBreakpointChange);
      this._cartUpsellsMq = null;
    }
  }

  private _onWishlistUpdated = (): void => {
    // Ré-exécute loadMany("wishlist") pour refléter le nouvel état.
    void this.loadMany("wishlist");
  };

  private _onCartUpdated = (): void => {
    // Ré-exécute loadMany("cart-upsells") quand le panier change (ajout /
    // retrait / merge) — le pivot product (1er item du cart) peut avoir
    // changé, les recommandations aussi.
    this.loadManyWhenAllowed("cart-upsells");
  };

  private loadManyWhenAllowed(kind: string): void {
    if (kind === "cart-upsells" && shouldSkipHiddenCartUpsells(this)) {
      this.removePreviousClones();
      this.setAttribute("data-lmdr-multi-template", "true");
      this.style.display = "none";
      this.setAttribute("data-state", "default");
      this.dispatchEvent(
        new CustomEvent("smootify:cart-upsells_count", {
          bubbles: true,
          detail: { count: 0, skipped: "mobile-hidden" },
        }),
      );
      this.watchCartUpsellsDesktopBreakpoint();
      return;
    }
    void this.loadMany(kind);
  }

  private watchCartUpsellsDesktopBreakpoint(): void {
    if (
      this._cartUpsellsMq ||
      typeof window === "undefined" ||
      typeof window.matchMedia !== "function"
    ) {
      return;
    }
    this._cartUpsellsMq = window.matchMedia("(min-width: 768px)");
    if (typeof this._cartUpsellsMq.addEventListener === "function") {
      this._cartUpsellsMq.addEventListener(
        "change",
        this._onCartUpsellsBreakpointChange,
      );
    } else {
      this._cartUpsellsMq.addListener?.(this._onCartUpsellsBreakpointChange);
    }
  }

  // -------------------------------------------------------------------------
  //  Handle resolution
  // -------------------------------------------------------------------------

  private resolveHandle(): string | null {
    const attrHandle =
      this.getAttribute("data-handle") ?? this.getAttribute("handle");
    if (attrHandle && attrHandle.length > 0) return attrHandle;

    const shopifyId = this.getAttribute("shopify-id");
    if (shopifyId && shopifyId.length > 0) {
      // The scanner/boot code may later translate this into a handle via a
      // secondary lookup. For now we surface it on the dataset so observers
      // can react.
      this.dataset["shopifyId"] = shopifyId;
    }

    // Valeurs spéciales data-id utilisées par Smootify pour les blocs
    // "upsells in cart / related / last-viewed / wishlist" — pas liés à UN
    // produit précis : on ne charge PAS le fallback URL / href.
    const dataId = this.getAttribute("data-id");
    const SPECIAL_IDS = new Set([
      "cart-upsells",
      "related",
      "last-viewed",
      "wishlist",
      "filter",
    ]);
    if (dataId !== null && SPECIAL_IDS.has(dataId)) {
      return null;
    }

    // Pages COLLECTION : Webflow CMS pose `data-id="gid://shopify/Product/..."`
    // sur chaque card. Le GID ne sert à rien pour la Storefront query
    // `product(handle: ...)`, mais le handle est dans le `<a href="/product/...">`
    // du même card. On le parse. Marche aussi sur d'autres templates Webflow
    // qui incluent le lien produit.
    const linkHandle = this.extractHandleFromProductLink();
    if (linkHandle) return linkHandle;

    return resolveProductHandleFromURL();
  }

  /**
   * Cherche un lien `<a href="/{productsBase}/<handle>">` dans le sous-arbre
   * du custom element et en extrait le handle. Utilisé comme fallback pour les
   * cards de collection où le handle n'est pas exposé en attribut direct —
   * seul un `data-id="gid://shopify/Product/..."` est posé par le CMS Webflow.
   */
  private extractHandleFromProductLink(): string | null {
    const base = config.productsBase.replace(/^\/+|\/+$/g, "");
    if (!base) return null;
    // Match attribute contains the base segment (cherche à la fois les href
    // relatifs "/product/xxx" et absolus "https://shop/product/xxx").
    const selector = `a[href*="/${base}/"]`;
    const anchors = Array.from(this.querySelectorAll<HTMLAnchorElement>(selector));
    const re = new RegExp(`/${base}/([^/?#]+)`);
    for (const a of anchors) {
      // `.getAttribute` plutôt que `.href` pour préserver le path relatif
      // tel qu'authored par Webflow.
      const href = a.getAttribute("href") ?? "";
      const m = href.match(re);
      if (m && m[1]) {
        try {
          return decodeURIComponent(m[1]);
        } catch {
          return m[1];
        }
      }
    }
    return null;
  }

  /**
   * Cards issues du CMS Webflow et clones dynamiques : ne jamais hydrater un
   * produit absent de la publication Headless, même si Shopify Storefront le
   * retourne encore par handle.
   */
  private shouldGateAgainstHeadless(): boolean {
    if (this.hasAttribute("data-lmdr-skip-headless-gate")) return false;
    if (this.hasAttribute("data-lmdr-multi-clone")) return true;
    const dataId = this.getAttribute("data-id") ?? "";
    return dataId.startsWith("gid://shopify/Product/") && this.closest(".w-dyn-item") !== null;
  }

  private hideUnavailableCard(): void {
    const dynItem = this.closest<HTMLElement>(".w-dyn-item");
    if (dynItem && dynItem.contains(this)) {
      dynItem.style.display = "none";
      return;
    }
    this.style.display = "none";
  }

  // -------------------------------------------------------------------------
  //  Load + hydrate
  // -------------------------------------------------------------------------

  private async loadProduct(): Promise<void> {
    const handle = this.resolveHandle();
    if (!handle) {
      warn("no handle resolved — leaving DOM untouched");
      this.setAttribute("data-state", "default");
      return;
    }

    try {
      if (this.shouldGateAgainstHeadless()) {
        try {
          const isHeadless = await isHeadlessHandle(handle);
          if (!isHeadless) {
            warn(`product "${handle}" is not published on Headless — hiding card`);
            this.hideUnavailableCard();
            this.setAttribute("data-state", "default");
            return;
          }
        } catch (err) {
          // Fail-open: si le worker endpoint est indisponible, on garde le
          // comportement existant au lieu de masquer tout un listing.
          warn("headless gate failed — continuing with Storefront hydration", err);
        }
      }

      const data = await storefront.query<{ product: Product | null }>(
        PRODUCT_BY_HANDLE,
        { handle },
      );
      const product = data.product;
      if (!product) {
        warn(`product "${handle}" not found`);
        // Cas concret : produit passe en DRAFT / archive / retire de headless
        // apres que le handle a ete pose (related, last-viewed, wishlist,
        // cart-upsells, ou collection page avec CMS stale). Sans hide, la
        // clone reste visible avec le placeholder Webflow ("Heading / 15.99$
        // / Non disponible / Ajouter au panier") → ghost card. On hide only
        // les multi-clones (ou data-id="filter" card) pour ne pas casser la
        // page produit principale qui aurait son propre handling d error.
        if (this.hasAttribute("data-lmdr-multi-clone") || this.shouldGateAgainstHeadless()) {
          this.hideUnavailableCard();
        }
        this.setAttribute("data-state", "default");
        return;
      }

      this.product = product;
      this.selectedVariant = pickDefaultVariant(product);

      // Initial hydration pass.
      hydrateProduct({ root: this, product });
      hydrateOptions(this, product);
      if (this.selectedVariant) {
        syncSelectionToVariant(this, this.selectedVariant);
        hydrateVariant({ root: this, product, variant: this.selectedVariant });
      }
      applyConditions({
        root: this,
        state: buildConditionState({
          product,
          variant: this.selectedVariant,
        }),
      });

      if (typeof document !== "undefined") {
        document.dispatchEvent(
          new CustomEvent("smootify:product_loaded", {
            detail: { id: product.id, product },
          }),
        );
      }

      // Animation fade-in pour les clones filter (cascade Smootify-style).
      // CSS dans critical-style : opacity 0 → 1 avec transition 0.3s.
      this.classList.add("lmdr-card-loaded");
      // Cards lazy-hydratees (scroll, filter change) : reveal immediat.
      // Cards eager page-1 : revealed par le metronome dans loadMany.
      if (this.hasAttribute("data-lmdr-multi-clone")) {
        this.setAttribute("data-lmdr-revealed", "1");
      }
      // Libere les descendants bloques dans une transition visibility stuck
      // (cf. explication cote hydrateBatchEager).
      cancelStuckAnimations(this);

      // Track last-viewed — uniquement sur la page produit elle-même (pas dans
      // les cards related / last-viewed). Détection : le handle visité == URL.
      const urlHandle = resolveProductHandleFromURL();
      if (urlHandle && urlHandle === product.handle && !this.hasAttribute("data-lmdr-multi-clone")) {
        pushLastViewed(product.handle);
      }
    } catch (err) {
      warn("failed to load product", err);
    } finally {
      this.setAttribute("data-state", "default");
    }
  }

  /**
   * Flow "multi-produits" : cas data-id="related" / "last-viewed" / "cart-upsells".
   * On ne charge PAS un produit sur `this` (qui est le template) — on fetch N
   * handles, on clone `this` 1 fois par handle en donnant un `data-handle` précis,
   * et on laisse la hydratation normale de chaque clone faire le reste.
   */
  private async loadMany(kind: string): Promise<void> {
    try {
      // Clean-up des clones précédents (cas refresh wishlist après add/remove).
      this.removePreviousClones();

      const limitAttr = this.getAttribute("limit");
      // Pour wishlist / filter : on autorise un cap très large pour que
      // l'intégrateur voie TOUTE la boutique par défaut (ex: 1500+ produits
      // headless). Les perfs sont gérables car chaque clone fetch sa donnée
      // produit indépendamment (async, cache Storefront partagé).
      // cart-upsells (= "related" sur la page /panier) : cap absolu a 3.
      // Page panier = espace UI restreint, on veut au plus 3 suggestions
      // pour rester lisible. L integrator peut mettre limit="1" pour un slot
      // unique upsell (cas /panier sidebar) — c est respecte (cap superieur).
      // Si limit="10" est mis par erreur dans Webflow → cap a 3.
      const defaultLimit =
        kind === "wishlist" ? 50 :
        kind === "filter" ? 2000 :
        kind === "cart-upsells" ? 3 :
        6;
      const maxLimit =
        kind === "wishlist" ? 200 :
        kind === "filter" ? 5000 :
        kind === "cart-upsells" ? 3 :
        20;
      const limit = Math.max(
        1,
        Math.min(
          maxLimit,
          Number.parseInt(limitAttr ?? String(defaultLimit), 10) || defaultLimit,
        ),
      );

      let handles: string[] = [];
      // Prefetch Promise du page-1-products.json (kind="filter" uniquement).
      // Posee en parallele avec fetchHeadlessHandles pour gagner ~150-250ms.
      let page1Prefetch: Promise<Response | null> | null = null;
      if (kind === "last-viewed") {
        handles = getLastViewedHandles()
          // Évite d'afficher le produit courant dans sa propre liste.
          .filter((h) => h !== resolveProductHandleFromURL())
          .slice(0, limit);
      } else if (kind === "wishlist") {
        // Wishlist lue depuis localStorage (cf. wishlist-toggle.ts).
        handles = getWishlistHandles().slice(0, limit);
      } else if (kind === "filter") {
        // Page catalogue `/all-products` : on ne doit afficher QUE les produits
        // "cochés headless" (publication_ids côté Shopify Admin). La Storefront
        // API ne supporte pas ce filtre ; on passe par un endpoint custom du
        // worker qui retourne la whitelist des handles via l'Admin API.
        //
        // Source: GET {headlessHandlesUrl} (config.json) → `{ handles: [...] }`.
        // Fallback: si l'endpoint échoue, on se rabat sur Storefront `products()`
        // avec cap 250 (limite Shopify Storefront API par page).
        //
        // PARALLEL PREFETCH : on kickstart la requete page-1-products.json
        // DES MAINTENANT (sans attendre les handles). Les 2 fetch partent
        // simultanement. Le worker route /sync/* est cache 60s + gzip ~80%.
        const page1Url = resolvePage1Url();
        page1Prefetch = page1Url
          ? fetch(page1Url, { cache: "default" }).catch(() => null)
          : null;
        try {
          handles = await fetchHeadlessHandles(limit);
        } catch (err) {
          warn("loadMany: headless-handles fetch failed, fallback to products()", err);
          try {
            const fallbackLimit = Math.min(250, limit);
            const r = await storefront.query<{
              products: Array<{ handle: string }>;
            }>(ALL_PRODUCTS, { first: fallbackLimit });
            handles = (r.products ?? []).map((p) => p.handle).slice(0, limit);
          } catch (err2) {
            warn("loadMany: fallback products() also failed", err2);
          }
        }
      } else if (kind === "related" || kind === "cart-upsells") {
        // 2 sources pour le "pivot product" depuis lequel on fetch les
        // recommendations Shopify (productRecommendations) :
        //   - `related` : le produit de la page courante (URL /product/xxx).
        //     On a juste le handle → 1 query PRODUCT_BY_HANDLE pour l ID.
        //   - `cart-upsells` : le DERNIER produit ajouté au panier.
        //     IMPORTANT : Shopify Storefront retourne `cart.lines` dans
        //     l ordre DERNIER AJOUTÉ EN PREMIER (lines[0] = newest).
        //     On a deja l ID dans cart.lines[0].merchandise.product.id →
        //     SKIP du PRODUCT_BY_HANDLE pivot (gain 1 query reseau).
        // Si aucun pivot → aucune reco → liste vide (template caché).
        let pivotId: string | null = null;
        try {
          if (kind === "related") {
            const pivotHandle = resolveProductHandleFromURL();
            if (pivotHandle) {
              const r1 = await storefront.query<{ product: { id: string } | null }>(
                PRODUCT_BY_HANDLE,
                { handle: pivotHandle },
              );
              pivotId = r1.product?.id ?? null;
            }
          } else {
            // kind === "cart-upsells" : ID directement dispo dans le cart
            const cart = getCurrentCart();
            pivotId =
              (cart?.lines?.[0]?.merchandise as { product?: { id?: string } } | undefined)
                ?.product?.id ?? null;
          }
          if (pivotId) {
            const r2 = await storefront.query<{
              productRecommendations: Array<{ handle: string }> | null;
            }>(PRODUCT_RECOMMENDATIONS, { productId: pivotId });
            const recs = r2.productRecommendations ?? [];
            handles = recs
              .map((p) => p.handle)
              // Évite d afficher un produit déjà dans le panier.
              .filter((h) => {
                if (kind !== "cart-upsells") return true;
                const cart = getCurrentCart();
                return !cart?.lines?.some(
                  (l) => l.merchandise?.product?.handle === h,
                );
              })
              .slice(0, limit);
          }
        } catch (err) {
          warn("loadMany: failed to fetch recommendations", err);
        }
      }

      // Events qui alimentent le condition layer (has-* / no-items-in-*).
      const COUNT_KINDS = new Set([
        "related",
        "last-viewed",
        "wishlist",
        "cart-upsells",
        "filter",
      ]);

      if (handles.length === 0) {
        // Aucun produit à afficher → on cache le bloc template.
        this.setAttribute("data-lmdr-multi-template", "true");
        this.style.display = "none";
        this.setAttribute("data-state", "default");
        if (COUNT_KINDS.has(kind)) {
          // Signal au condition layer : pas d'items.
          this.dispatchEvent(
            new CustomEvent(`smootify:${kind}_count`, {
              bubbles: true,
              detail: { count: 0 },
            }),
          );
        }
        return;
      }

      // Préparation : on cache le template et on insère les clones APRÈS lui.
      this.setAttribute("data-lmdr-multi-template", "true");
      this.style.display = "none";

      const parent = this.parentElement;
      if (!parent) return;

      // Métadonnées (prix + search blob + tri + facets) posées par le worker
      // pour filter client-side instantané sans avoir à hydrater chaque card.
      const meta = headlessHandlesCache?.data;
      const pricesIndex = meta?.prices ?? {};
      const searchIndex = meta?.search ?? {};
      const createdAtIndex = meta?.createdAt ?? {};
      const collectionsIndex = meta?.collections ?? {};
      const tagsIndex = meta?.tags ?? {};

      let lastInserted: Element = this;
      for (const handle of handles) {
        const clone = this.cloneNode(true) as SmootifyProductElement;
        clone.removeAttribute("data-lmdr-multi-template");
        clone.setAttribute("data-lmdr-multi-clone", "true");
        // Origine du clone (filter / wishlist / related / …) — utilisé par
        // le layer filter (attributes/filter.ts) pour cibler uniquement les
        // clones catalogue.
        clone.setAttribute("data-lmdr-multi-kind", kind);
        // On fixe le handle pour que le flow single-product prenne priorité.
        clone.setAttribute("data-handle", handle);
        // On retire le data-id spécial pour éviter la récursion.
        clone.setAttribute("data-id", "");
        clone.style.removeProperty("display");
        // Pose les bornes prix en data-attrs pour le filter client-side
        // (utilisé par attributes/filter.ts → matchesFilter).
        const priceTuple = pricesIndex[handle];
        if (priceTuple) {
          clone.setAttribute("data-lmdr-price-min", String(priceTuple[0]));
          clone.setAttribute("data-lmdr-price-max", String(priceTuple[1]));
        }
        // Pose le search blob (title + tags lowercase) pour le filter search.
        const searchBlob = searchIndex[handle];
        if (searchBlob) {
          clone.setAttribute("data-lmdr-search", searchBlob);
        }
        // Pose createdAt (ms) pour tri "newest"
        const ts = createdAtIndex[handle];
        if (typeof ts === "number") {
          clone.setAttribute("data-lmdr-created-at", String(ts));
        }
        // Pose collections (handles séparés par virgule) pour facet collection
        const colHandles = collectionsIndex[handle];
        if (colHandles && colHandles.length > 0) {
          clone.setAttribute("data-lmdr-collections", colHandles.join(","));
        }
        // Pose tags (lowercase, séparés par virgule) pour facet tag
        const tagHandles = tagsIndex[handle];
        if (tagHandles && tagHandles.length > 0) {
          clone.setAttribute("data-lmdr-tags", tagHandles.join(","));
        }
        // Retire l'id du template (Webflow pose un id `w-node-XXX` unique) —
        // cloner le conserverait et produirait N éléments avec le même id, ce
        // qui casse le sélecteur CSS Webflow (`#w-node-XXX { ... }`) et est
        // invalide HTML. On le retire sur les enfants aussi par récursivité.
        stripDuplicateIds(clone);
        // PERF cart-upsells / related : on bloque le loadProduct individuel
        // de chaque clone pour les hydrater en BATCH ensuite (1 seule query
        // au lieu de N). Sans ce flag, connectedCallback() lance loadProduct.
        // Pour kind="filter" la lazy hydration gere ce cas via data-handle +
        // data-lmdr-multi-kind="filter" sans toucher a _loaded.
        if (kind === "cart-upsells" || kind === "related") {
          (clone as unknown as { _loaded: boolean })._loaded = true;
        }
        parent.insertBefore(clone, lastInserted.nextSibling);
        lastInserted = clone;
      }

      // PERF : pour cart-upsells / related, hydrate en BATCH via 1 seul fetch
      // PRODUCTS_LIGHT_BY_HANDLES (au lieu de N PRODUCT_BY_HANDLE FULL).
      // Gain : 5 queries → 2 queries par bloc cart-upsells. Sur la page panier
      // avec 2 blocs cart-upsells : 10 queries → 4 queries.
      //
      // FALLBACK robuste : si la batch query echoue (network error, DLP,
      // Storefront rate-limit, query syntax issue), on reset _loaded=false
      // sur les clones non hydrates et on lance leur loadProduct individuel.
      // Sans ce fallback, les clones resteraient en data-state="loading"
      // forever, donc invisibles, donc pas d upsell affiche → degradation UX.
      if (kind === "cart-upsells" || kind === "related") {
        const newClones = Array.from(
          parent.querySelectorAll<SmootifyProductElement>(
            `smootify-product[data-lmdr-multi-clone="true"][data-lmdr-multi-kind="${kind}"]`,
          ),
        );
        const triggerIndividualFallback = (): void => {
          for (const clone of newClones) {
            if (clone.product) continue; // deja hydrate (succes partiel batch)
            (clone as unknown as { _loaded: boolean })._loaded = false;
            try {
              clone.hydrateLazyNow();
            } catch {
              // Last-resort: ignore, le clone restera en loading mais
              // au moins le navigateur ne crash pas.
            }
          }
        };
        void hydrateBatchEager(newClones)
          .then(() => {
            // Verifier qu au moins 1 clone a recu son product. Sinon batch a
            // techniquement reussi mais Shopify n a renvoye aucun match
            // (ex: query syntax cassee, produits draft, etc.). Fallback.
            const anyHydrated = newClones.some((c) => !!c.product);
            if (!anyHydrated && newClones.length > 0) {
              warn(`hydrateBatchEager(${kind}) returned 0 products, fallback`);
              triggerIndividualFallback();
            }
          })
          .catch((err) => {
            warn(`hydrateBatchEager(${kind}) failed, fallback individual`, err);
            triggerIndividualFallback();
          });
      }

      if (COUNT_KINDS.has(kind)) {
        // Signal au condition layer qu'on a des items.
        this.dispatchEvent(
          new CustomEvent(`smootify:${kind}_count`, {
            bubbles: true,
            detail: { count: handles.length },
          }),
        );
      }

      // LAZY HYDRATION pour kind="filter" : 1500+ clones, on n'hydrate pas
      // tout au boot. Stratégie hybride OPTIMISÉE :
      //   1. EAGER BATCH : 1 seul fetch Storefront `products(first:80, query:"handle:A OR ...")`
      //      pour les 80 premiers clones. Au lieu de 80 fetches PRODUCT_BY_HANDLE,
      //      on en fait 1. Gain ~600-800ms.
      //   2. IntersectionObserver : observe les autres clones, trigger
      //      `hydrateLazyNow` quand ils entrent dans le viewport.
      //   3. STAGGER : animation-delay par index pour cascade fade-in.
      if (kind === "filter") {
        const newClones = Array.from(
          parent.querySelectorAll<SmootifyProductElement>(
            `smootify-product[data-lmdr-multi-clone="true"][data-lmdr-multi-kind="${kind}"]`,
          ),
        );
        // EAGER_COUNT = 40 (page 1 only). Avant : 80 (page 1 + page 2), mais
        // page 2 n est pas visible avant pagination → on l hydrate en lazy via
        // IntersectionObserver / event filter_count quand l user pagine.
        const EAGER_COUNT = 40;
        // Strategy : essayer d abord /page-1-products.json (pre-build worker
        // CDN-cache, ~150ms). Si ok, hydrate sans fetch Storefront. Sinon
        // fallback sur batch Storefront direct (PRODUCTS_LIGHT_BY_HANDLES).
        // Apres hydrate, cascade metronome 100ms par card (decouple du
        // timing image CDN) pose data-lmdr-revealed="1" card par card.
        const eagerClones = newClones.slice(0, EAGER_COUNT);
        void hydrateFromPage1OrFallback(eagerClones, page1Prefetch)
          .then(() => {
            startCascadeMetronome(eagerClones);
          })
          .catch((err) => {
            warn("hydrate eager failed, fallback to individual", err);
            for (const clone of eagerClones) {
              if (typeof clone.hydrateLazyNow === "function") {
                clone.hydrateLazyNow();
              }
            }
            // Metronome meme en fallback : les cards sont revealed via le
            // hydrateLazyNow individuel, mais on pose quand meme le revealed
            // flag pour evitir de laisser certaines cards hidden.
            startCascadeMetronome(eagerClones);
          });

        // IntersectionObserver pour les non-eager (lazy hydratation au scroll)
        // Setup retarde de 200ms : laisse le temps a refreshFilters d appliquer
        // pagination CSS (.lmdr-pagination-hidden) avant que IO ne fire pour
        // tous les clones empiles top:0. Sinon ~30 cards page 2+ se font
        // hydrater inutilement (= 30 storefront calls + 30 hydratations JS).
        if (typeof IntersectionObserver !== "undefined") {
          const setupObserver = (): void => {
            const observer = new IntersectionObserver(
              (entries) => {
                for (const entry of entries) {
                  if (!entry.isIntersecting) continue;
                  const el = entry.target as SmootifyProductElement;
                  observer.unobserve(el);
                  if (typeof el.hydrateLazyNow === "function") {
                    el.hydrateLazyNow();
                  }
                }
              },
              { rootMargin: "800px 0px", threshold: 0.01 },
            );
            for (let i = EAGER_COUNT; i < newClones.length; i++) {
              observer.observe(newClones[i]!);
            }
          };
          window.setTimeout(setupObserver, 200);

          // Re-trigger hydratation quand pagination/filtre change : on
          // ecoute l event smootify:filter_count emis par filter.ts apres
          // chaque applyFilters(). Defere l hydratation des cards qui
          // viennent de re-devenir visibles (page nav, filter change, etc).
          // 1 listener global, debounced 80ms pour batch les rafales.
          let rehydrateTimer: number | undefined;
          document.addEventListener("smootify:filter_count", () => {
            if (rehydrateTimer !== undefined) window.clearTimeout(rehydrateTimer);
            rehydrateTimer = window.setTimeout(() => {
              for (let i = EAGER_COUNT; i < newClones.length; i++) {
                const c = newClones[i]!;
                if (
                  !c.classList.contains("lmdr-pagination-hidden") &&
                  !c.classList.contains("lmdr-filter-hidden") &&
                  typeof c.hydrateLazyNow === "function"
                ) {
                  c.hydrateLazyNow();
                }
              }
            }, 80);
          });
        }
      }

      this.setAttribute("data-state", "default");
    } catch (err) {
      warn("loadMany failed", err);
      this.setAttribute("data-state", "default");
    }
  }

  /**
   * Retire les clones déjà insérés par une précédente exécution de loadMany
   * sur ce template. Les clones sont marqués `data-lmdr-multi-clone` et
   * suivent directement le template dans le DOM (ou ses siblings). On se
   * limite aux siblings directs pour rester local au template.
   */
  private removePreviousClones(): void {
    const parent = this.parentElement;
    if (!parent) return;
    // On retire tous les siblings clones — quelle que soit leur position —
    // pour gérer aussi le cas où un autre code aurait déplacé le template.
    const clones = parent.querySelectorAll<HTMLElement>(
      ':scope > smootify-product[data-lmdr-multi-clone="true"]',
    );
    clones.forEach((c) => {
      c.remove();
    });
  }

  private applyVariant(variant: ProductVariant): void {
    this.selectedVariant = variant;
    if (!this.product) return;

    hydrateVariant({ root: this, product: this.product, variant });
    applyConditions({
      root: this,
      state: buildConditionState({
        product: this.product,
        variant,
      }),
    });

    // Sync avec le slider principal : si la variante a une image, on active
    // la slide correspondante (matching par URL). Fallback : si l'image de
    // la variante n'est pas dans le slider (variante image custom), on
    // remplace l'image de la slide 0 par l'image variante.
    this.syncSliderToVariant(variant);
  }

  /**
   * Cherche la slide du `.w-slider[product="images"]` interne qui match
   * `variant.image.url` (via `data-lmdr-zoom-src` posé par hydrateWebflowSlider).
   * Si trouvée → active cette slide. Sinon → override l'image de la slide
   * active avec `variant.image.url`.
   */
  private syncSliderToVariant(variant: ProductVariant): void {
    const variantImgUrl = variant?.image?.url;
    if (!variantImgUrl) return;
    const slider = this.querySelector<HTMLElement>(
      ':scope [product="images"][data-lmdr-images-count]',
    );
    if (!slider) return;
    // Cherche la slide dont data-lmdr-zoom-src correspond à l'URL variant.
    const slides = slider.querySelectorAll<HTMLElement>(
      "[data-lmdr-slide-index]",
    );
    for (const slide of Array.from(slides)) {
      if (slide.getAttribute("data-lmdr-zoom-src") === variantImgUrl) {
        const idx = Number.parseInt(
          slide.getAttribute("data-lmdr-slide-index") ?? "0",
          10,
        );
        // Utilise le setActiveSlide exporté via un custom event pour éviter
        // un import circulaire.
        slider.dispatchEvent(
          new CustomEvent("lmdr:set-active-slide", {
            detail: { idx },
            bubbles: true,
          }),
        );
        return;
      }
    }
    // Pas de slide matchant → override l'image de la slide active.
    const activeIdx = Number.parseInt(
      slider.getAttribute("data-lmdr-slide-active") ?? "0",
      10,
    );
    const activeSlide = slides[activeIdx];
    if (activeSlide) {
      activeSlide.style.backgroundImage = `url(${JSON.stringify(variantImgUrl)})`;
      activeSlide.setAttribute("data-lmdr-zoom-src", variantImgUrl);
    }
  }
}

/**
 * Hydratation BATCH des N premiers clones via 1 SEUL fetch Storefront
 * `products(first:N, query:"handle:A OR handle:B OR ...")`.
 *
 * Gain perf majeur : au lieu de N fetches PRODUCT_BY_HANDLE séquentialisés
 * par le sémaphore (~600-800ms), on fait 1 fetch (~150-250ms).
 *
 * Pour chaque produit retourné, trouve le clone matching par handle et
 * applique la pleine hydratation (hydrateProduct + variant + options +
 * conditions + dispatch event).
 */
/**
 * Metronome cascade reveal : tick 100ms par card, dans l ordre DOM.
 *
 * Decouple le timing du reveal de celui du chargement CDN des images : meme
 * si les images arrivent en bursts (HTTP/2 multiplex), les cards apparaissent
 * a intervalles reguliers de 100ms apres l end d hydrate. Les images fade-in
 * quand elles chargent (attribute data-lmdr-img-ready pose par applyImage) —
 * si une image arrive avant le tick de sa card, elle est deja ready au moment
 * ou la card est revealed ; sinon skeleton reste visible jusqu au load.
 *
 * Si le clone a deja data-lmdr-revealed (cas re-call apres fallback), on skip
 * le stagger pour cette card (deja visible). Idempotent.
 */
const CASCADE_METRONOME_MS = 100;
function startCascadeMetronome(clones: SmootifyProductElement[]): void {
  let tick = 0;
  for (const clone of clones) {
    if (clone.hasAttribute("data-lmdr-revealed")) continue;
    window.setTimeout(() => {
      clone.setAttribute("data-lmdr-revealed", "1");
    }, tick * CASCADE_METRONOME_MS);
    tick++;
  }
}

/**
 * Cancel toutes les Web Animations actives sur les descendants d un clone.
 *
 * Contexte du bug :
 *   - Mon CSS critical-style pose `visibility: hidden !important` sur
 *     smootify-product[data-lmdr-multi-clone]:not([data-state="default"]).
 *   - Quand la hydratation termine et passe data-state a "default", la
 *     rule ne matche plus, visibility passe de hidden a visible.
 *   - Webflow pose `transition: all` sur `.product-card` donc le changement
 *     de visibility declenche une transition animee de 300ms.
 *   - Cette transition genere des Web Animations avec `fill: backwards` et
 *     keyframes `[0: hidden, 1: visible]`. Bug constate : certaines restent
 *     bloquees a currentTime=0 en playState="running" → la card + img
 *     restent invisibles ET unclickable, meme si la rule CSS ne matche plus.
 *
 * Fix : cancel() toutes les Web Animations sur tous les descendants.
 * Idempotent : si aucune animation, no-op. Pas de cout perf significatif
 * (maxi 2 animations par element, getAnimations() est natif rapide).
 */
function cancelStuckAnimations(root: Element): void {
  if (typeof root.getAnimations !== "function") return;
  try {
    const all = root.getAnimations({ subtree: true });
    for (const anim of all) {
      // Skip CSS Transitions (animationName=undefined) : ce sont nos propres
      // transitions opacity / transform, pas les Webflow IX2 animations
      // qui causaient le bug visibility:hidden initial.
      const name = (anim as Animation & { animationName?: string }).animationName;
      if (typeof name !== "string" || name.length === 0) continue;
      // Skip nos propres animations LMDR (lmdr-skeleton-shimmer)
      if (name.startsWith("lmdr-")) continue;
      anim.cancel();
    }
  } catch {
    // Defensive : getAnimations peut throw sur certains vieux browsers.
  }
}

/**
 * Resolve l URL du worker endpoint /page-1-products.json en s appuyant
 * sur la meme logique que headlessHandlesUrl (derive du bundle URL).
 */
function resolvePage1Url(): string | null {
  if (typeof config.headlessHandlesUrl === "string" && config.headlessHandlesUrl.length > 0) {
    return config.headlessHandlesUrl.replace(/headless-handles\.json.*$/, "page-1-products.json");
  }
  if (typeof document === "undefined") return null;
  const scripts = Array.from(document.scripts);
  for (const s of scripts) {
    if (s.src && /lmdr-commerce/.test(s.src)) {
      const u = new URL(s.src);
      return `${u.origin}/sync/page-1-products.json`;
    }
  }
  return null;
}

/**
 * Fast path : fetch /page-1-products.json (CDN-cache, ~150ms warm) au lieu
 * de PRODUCTS_LIGHT_BY_HANDLES (700ms direct Storefront browser). Hydrate les
 * 40 premieres cards a partir du payload pre-build server-side. Fallback sur
 * hydrateBatchEager (Storefront direct) si l endpoint echoue ou ne contient
 * pas tous les handles attendus.
 */
async function hydrateFromPage1OrFallback(
  clones: SmootifyProductElement[],
  prefetch?: Promise<Response | null> | null,
): Promise<void> {
  if (clones.length === 0) return;
  let products: Product[] = [];
  // Prefer le prefetch lance en parallele avec headless-handles dans loadMany.
  // Si pas fourni ou echec, fallback sur fetch direct ici.
  if (prefetch) {
    try {
      const resp = await prefetch;
      if (resp && resp.ok) {
        const json = (await resp.json()) as { products?: Product[] };
        products = json.products ?? [];
      }
    } catch {
      // fall through to standalone fetch
    }
  }
  if (products.length === 0) {
    const url = resolvePage1Url();
    if (url) {
      try {
        const resp = await fetch(url, { cache: "default" });
        if (resp.ok) {
          const json = (await resp.json()) as { products?: Product[] };
          products = json.products ?? [];
        }
      } catch {
        // Network error → fallback path below
      }
    }
  }

  if (products.length === 0) {
    return hydrateBatchEager(clones);
  }

  // Mark + hydrate. fetchpriority=high sur les 8 premieres cards (above
  // fold) pour que browser priorise leur image vs Webflow assets et CDN
  // fonts. Cards 9+ restent default (lazy + low priority).
  const HIGH_PRIORITY_COUNT = 8;
  clones.forEach((c, idx) => {
    c.setAttribute("data-state", "loading");
    c.setAttribute("data-lmdr-allow-hydration", "true");
    if (idx < HIGH_PRIORITY_COUNT) {
      c.setAttribute("data-lmdr-priority", "high");
    }
  });

  const byHandle = new Map<string, Product>();
  for (const p of products) byHandle.set(p.handle, p);

  // Compute clones not in worker payload → hydrate them via Storefront fallback
  const missing: SmootifyProductElement[] = [];
  for (const clone of clones) {
    const handle = clone.getAttribute("data-handle") ?? "";
    const product = byHandle.get(handle);
    if (!product) {
      missing.push(clone);
      continue;
    }
    try {
      (clone as unknown as { _loaded: boolean })._loaded = true;
      clone.product = product;
      clone.selectedVariant = pickDefaultVariant(product);
      hydrateProduct({ root: clone, product });
      hydrateOptions(clone, product);
      if (clone.selectedVariant) {
        syncSelectionToVariant(clone, clone.selectedVariant);
        hydrateVariant({ root: clone, product, variant: clone.selectedVariant });
      }
      applyConditions({
        root: clone,
        state: buildConditionState({ product, variant: clone.selectedVariant }),
      });
      clone.classList.add("lmdr-card-loaded");
      clone.setAttribute("data-state", "default");
      cancelStuckAnimations(clone);
      document.dispatchEvent(
        new CustomEvent("smootify:product_loaded", {
          detail: { id: product.id, product },
        }),
      );
    } catch (err) {
      warn("hydrateFromPage1: failed for", handle, err);
      clone.setAttribute("data-state", "default");
    }
  }

  // Hydrate les clones manquants via Storefront LIGHT batch
  if (missing.length > 0) {
    await hydrateBatchEager(missing);
  }
}

async function hydrateBatchEager(
  clones: SmootifyProductElement[],
): Promise<void> {
  if (clones.length === 0) return;
  const handles = clones
    .map((c) => c.getAttribute("data-handle") ?? "")
    .filter((h) => h.length > 0);
  if (handles.length === 0) return;

  // Marque les clones comme "loading" + permet hydratation (skip skip)
  clones.forEach((c) => {
    c.setAttribute("data-state", "loading");
    c.setAttribute("data-lmdr-allow-hydration", "true");
  });

  // Build Shopify search query "handle:A OR handle:B OR ..."
  // Limite Shopify : query string max ~1KB, ~80 handles fit confortablement.
  const query = handles.map((h) => `handle:${h}`).join(" OR ");

  // PRODUCTS_LIGHT_BY_HANDLES (pas FULL) : ne fetch que les fields necessaires
  // pour rendre une card (title/handle/vendor/featuredImage/priceRange + 6
  // variants pour thumbs). Gain : 73KB vs 257KB et 600ms vs 1000ms (curl).
  // Si une card lazy a besoin de la full data (rare, ex: page produit ouverte
  // dans cette card par hasard), loadProduct() refera la query FULL.
  const data = await storefront.query<{ products: Product[] }>(
    PRODUCTS_LIGHT_BY_HANDLES,
    { query, first: handles.length },
    { cache: false },
  );
  const products = data.products ?? [];

  // Index par handle pour lookup O(1)
  const byHandle = new Map<string, Product>();
  for (const p of products) byHandle.set(p.handle, p);

  // Distribute aux clones + hydrate
  for (const clone of clones) {
    const handle = clone.getAttribute("data-handle") ?? "";
    const product = byHandle.get(handle);
    if (!product) {
      // Pas trouvé → fallback individuel
      if (typeof clone.hydrateLazyNow === "function") clone.hydrateLazyNow();
      continue;
    }
    try {
      // Mark _loaded pour empêcher hydrateLazyNow ultérieur
      (clone as unknown as { _loaded: boolean })._loaded = true;
      clone.product = product;
      clone.selectedVariant = pickDefaultVariant(product);
      hydrateProduct({ root: clone, product });
      hydrateOptions(clone, product);
      if (clone.selectedVariant) {
        syncSelectionToVariant(clone, clone.selectedVariant);
        hydrateVariant({ root: clone, product, variant: clone.selectedVariant });
      }
      applyConditions({
        root: clone,
        state: buildConditionState({
          product,
          variant: clone.selectedVariant,
        }),
      });
      clone.classList.add("lmdr-card-loaded");
      clone.setAttribute("data-state", "default");
      // Cancel les animations CSS/Web Animations restees stuck sur les
      // descendants de la clone. Bug constate : Webflow IX2 ou une transition
      // `all 300ms` sur .product-card reste bloquee avec visibility:hidden
      // (keyframes stuck a offset 0), rendant l image IMG invisible et
      // unclickable apres hydratation. Cancel libere l element → vis visible.
      cancelStuckAnimations(clone);
      // Dispatch event pour layer filter (refresh count, etc.)
      document.dispatchEvent(
        new CustomEvent("smootify:product_loaded", {
          detail: { id: product.id, product },
        }),
      );
    } catch (err) {
      warn("hydrateBatchEager: failed to hydrate clone", handle, err);
      clone.setAttribute("data-state", "default");
    }
  }
}

// ---------------------------------------------------------------------------
//  Headless handles whitelist (pour data-id="filter")
// ---------------------------------------------------------------------------

/**
 * Cache in-memory des handles headless (whitelist Shopify publication_ids).
 * Inclut aussi les bornes prix globales et la map handle → [priceMin, priceMax]
 * pour permettre le filter prix client-side même sur les cards lazy non-hydratées.
 * TTL 60s — le worker recalcule de son côté toutes les 5 min.
 */
export interface HeadlessHandlesPayload {
  handles: string[];
  priceMin: number;
  priceMax: number;
  prices: Record<string, [number, number]>;
  /** handle → "title tag1 tag2..." (lowercase) pour search instant. */
  search: Record<string, string>;
  /** handle → timestamp Unix ms du createdAt (pour tri "newest"). */
  createdAt?: Record<string, number>;
  /** handle → array de collection handles (pour facet collection). */
  collections?: Record<string, string[]>;
  /** handle → array de tags lowercase (pour facet tag). */
  tags?: Record<string, string[]>;
}
let headlessHandlesCache:
  | { data: HeadlessHandlesPayload; expiresAt: number }
  | null = null;
let headlessHandlesInFlight: Promise<HeadlessHandlesPayload> | null = null;

/** Accesseur public utilisé par filter.ts pour lire les bornes globales. */
export function getHeadlessMeta(): HeadlessHandlesPayload | null {
  return headlessHandlesCache?.data ?? null;
}

/**
 * Résout l'URL du endpoint headless-handles :
 *   1. config.headlessHandlesUrl si renseigné (string non-vide)
 *   2. Sinon : auto-dérivé de l'URL du bundle courant — on retire
 *      `/lmdr-commerce/<version>/index.js` et on préfixe `/sync/headless-handles.json`.
 *   3. Si aucun `<script src="...lmdr-commerce...">` trouvé → null.
 */
function resolveHeadlessHandlesUrl(): string | null {
  if (config.headlessHandlesUrl) return config.headlessHandlesUrl;
  if (typeof document === "undefined") return null;
  const scripts = document.querySelectorAll<HTMLScriptElement>("script[src]");
  for (const s of Array.from(scripts)) {
    if (s.src.includes("/lmdr-commerce/") && s.src.includes("/index.js")) {
      const base = s.src.replace(/\/lmdr-commerce\/[^/]+\/index\.js.*$/, "");
      return `${base}/sync/headless-handles.json`;
    }
  }
  return null;
}

async function fetchHeadlessPayload(): Promise<HeadlessHandlesPayload> {
  if (
    headlessHandlesCache &&
    headlessHandlesCache.expiresAt > Date.now()
  ) {
    return headlessHandlesCache.data;
  }
  if (headlessHandlesInFlight) {
    return headlessHandlesInFlight;
  }
  const url = resolveHeadlessHandlesUrl();
  if (!url) {
    throw new Error("headless-handles endpoint URL not configured");
  }

  headlessHandlesInFlight = (async () => {
    const res = await fetch(url, { credentials: "omit" });
    if (!res.ok) {
      throw new Error(`headless-handles: HTTP ${res.status}`);
    }
    const json = (await res.json()) as Partial<HeadlessHandlesPayload>;
    const handles = Array.isArray(json.handles) ? json.handles : [];
    const data: HeadlessHandlesPayload = {
      handles,
      priceMin: typeof json.priceMin === "number" ? json.priceMin : 0,
      priceMax: typeof json.priceMax === "number" ? json.priceMax : 500,
      prices: json.prices ?? {},
      search: json.search ?? {},
      createdAt: json.createdAt ?? {},
      collections: json.collections ?? {},
      tags: json.tags ?? {},
    };
    headlessHandlesCache = {
      data,
      expiresAt: Date.now() + 60 * 1000,
    };
    return data;
  })();

  try {
    return await headlessHandlesInFlight;
  } finally {
    headlessHandlesInFlight = null;
  }
}

async function fetchHeadlessHandles(limit: number): Promise<string[]> {
  const data = await fetchHeadlessPayload();
  return data.handles.slice(0, limit);
}

async function isHeadlessHandle(handle: string): Promise<boolean> {
  const data = await fetchHeadlessPayload();
  return data.handles.includes(handle);
}

// ---------------------------------------------------------------------------
//  Helpers file-level (hors de la classe pour lisibilité)
// ---------------------------------------------------------------------------

/**
 * Retire récursivement l'attribut `id` d'un clone — sauf l'identifiant racine
 * qu'on peut éventuellement préserver si utile (on le retire aussi par défaut).
 *
 * Rationale : Webflow pose des `id="w-node-..."` sur les grid items pour
 * adresser leur placement via CSS (`#w-node-XXX { grid-column: 3 / 5 }`, ...).
 * Ces ids sont **uniques** dans le HTML source. Lorsqu'on clone un template
 * pour le dupliquer N fois (wishlist / related / last-viewed / cart-upsells),
 * l'id est recopié — on se retrouve avec N nodes partageant le même id.
 *
 * Conséquences :
 *   1. HTML invalide (la spec exige des ids uniques).
 *   2. `document.getElementById` retourne seulement le premier.
 *   3. Les sélecteurs CSS `#id` matchent TOUS les clones → tous reçoivent
 *      le même placement grid (ex: tous en colonne 3-5), cassant le layout.
 *
 * On strip donc l'id du clone racine ET de tous ses descendants. Les autres
 * attributs (class, data-*, etc.) restent intacts — seuls les ids posent
 * problème en cas de duplication.
 */
function stripDuplicateIds(root: Element): void {
  if (root.hasAttribute("id")) root.removeAttribute("id");
  root.querySelectorAll<HTMLElement>("[id]").forEach((el) => {
    el.removeAttribute("id");
  });
}
